import i18nCountries from 'i18n-iso-countries';
import type { ProgrammeId } from '@/common/types';
import { Programmes } from '@/utils/constants';

const PRIORITY_COUNTRIES_BY_PROGRAMME: Record<Programmes, string[]> = {
  [Programmes.BAEC]: ['GB', 'US'],
  [Programmes.IBP]: ['ES', 'US'],
  [Programmes.AERC]: ['IE', 'US'],
};

const DEFAULT_PRIORITY_COUNTRIES = ['GB', 'US'];
const LOCALE = 'en';

type CountryOption = {
  value: string;
  label: string;
};

/**
 * Generates a sorted list of country options for use in dropdowns.
 * Priority countries appear at the top based on the programme:
 * All other countries are sorted alphabetically by their name.
 */
export const getCountriesOptions = (
  programmeId?: ProgrammeId,
): CountryOption[] => {
  const allCodes = Object.keys(i18nCountries.getAlpha2Codes());
  const priorityList = programmeId
    ? (PRIORITY_COUNTRIES_BY_PROGRAMME[programmeId] ??
      DEFAULT_PRIORITY_COUNTRIES)
    : DEFAULT_PRIORITY_COUNTRIES;
  const prioritySet = new Set(priorityList);

  const { priorityCountries, otherCountries } = allCodes.reduce<{
    priorityCountries: CountryOption[];
    otherCountries: CountryOption[];
  }>(
    (acc, code) => {
      const option = {
        value: code,
        label: i18nCountries.getName(code, LOCALE) || code,
      };

      if (prioritySet.has(code)) {
        acc.priorityCountries.push(option);
      } else {
        acc.otherCountries.push(option);
      }

      return acc;
    },
    { priorityCountries: [], otherCountries: [] },
  );

  priorityCountries.sort(
    (a, b) => priorityList.indexOf(a.value) - priorityList.indexOf(b.value),
  );

  otherCountries.sort((a, b) => a.label.localeCompare(b.label, LOCALE));

  return [...priorityCountries, ...otherCountries];
};
